import React, { useState } from 'react';
import { LocationAutocomplete } from './location-autocomplete';
import { MapDisplay } from './map-display';
import { LocationFeature, LocationSearchOptions } from '../../../types/location';
import { Card, CardContent, CardHeader, CardTitle } from '../card';
import { Button } from '../button';
import { Badge } from '../badge';
import { MapPin, X } from 'lucide-react';
import { cn } from '../utils';

interface LocationPickerProps {
    onLocationSelect: (location: LocationFeature) => void;
    selectedLocation?: LocationFeature;
    searchOptions?: LocationSearchOptions;
    className?: string;
    showMapPreview?: boolean;
    mapHeight?: number;
    title?: string;
    description?: string;
}

export function LocationPicker({
    onLocationSelect,
    selectedLocation,
    searchOptions,
    className,
    showMapPreview = true,
    mapHeight = 300,
    title = 'Choose Location',
    description = 'Search for a location and preview it on the map',
}: LocationPickerProps) {
    const [previewLocation, setPreviewLocation] = useState<LocationFeature | undefined>(selectedLocation);

    const handleLocationSelect = (location: LocationFeature) => {
        setPreviewLocation(location);
        onLocationSelect(location);
    };

    const handleClearLocation = () => {
        setPreviewLocation(undefined);
    };

    return (
        <div className={cn('space-y-4', className)}>
            <div>
                <h3 className="text-lg font-semibold">{title}</h3>
                <p className="text-sm text-muted-foreground">{description}</p>
            </div>

            <LocationAutocomplete
                onSelect={handleLocationSelect}
                onClear={handleClearLocation}
                searchOptions={searchOptions}
                placeholder="Search for a location..."
                value={previewLocation?.place_name || ''}
            />

            {previewLocation && (
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                                <MapPin className="w-5 h-5 text-primary mt-0.5" />
                                <div>
                                    <CardTitle className="text-base">{previewLocation.text}</CardTitle>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {previewLocation.place_name}
                                    </p>
                                    <div className="flex items-center space-x-2 mt-2">
                                        <Badge variant="outline" className="text-xs">
                                            {previewLocation.center[1].toFixed(4)}, {previewLocation.center[0].toFixed(4)}
                                        </Badge>
                                        {previewLocation.properties?.category && (
                                            <Badge variant="outline" className="text-xs">
                                                {previewLocation.properties.category}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleClearLocation}
                                className="p-1 h-auto"
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    </CardHeader>

                    {showMapPreview && (
                        <CardContent className="pt-0">
                            <MapDisplay
                                location={previewLocation}
                                height={mapHeight}
                                zoom={15}
                                showUserLocation={false}
                                interactive={true}
                            />
                        </CardContent>
                    )}
                </Card>
            )}
        </div>
    );
}
